// WindowScene.jsx
// The shared, read-only rendering of a user's plant window: room background,
// tint variant, and sprites at their normalized positions. Used by the public
// window page (/window/:userId), the signed-in garden page (/me), and the
// featured windows on the landing page.
//
// Geometry mirrors the iOS app's PlantCollectionView reference canvas of
// 390x701 points: sprites render 160pt wide (160/390 of the scene width)
// anchored bottom-center on their normalized {x, y} point, z-ordered by y so
// lower plants draw in front, and the square room art sits at the iOS
// -100pt horizontal offset (object-position ~82%).

// Packages, Dependencies, and Shared Libraries
import React from "react";
import { useNavigate } from "react-router-dom";
import { plantHasSpriteArt } from "../sharedFunctions";

// React Components
import PlantImage from "../Sprite/PlantImage";

// Images
import roomSunny from "../Images/Window/roomSunny.png";
import roomLoft from "../Images/Window/roomLoft.png";

// Mirrors Room.windowPlantCap in the iOS app (PlantModel.swift)
export const WINDOW_PLANT_CAP = 10;

export const DEFAULT_ROOM = { id: null, name: "My window", backgroundId: "sunny-day" };

// Default shelf slots for plants that haven't been arranged in the app yet.
// Derived from the iOS placement zones (PlantCollectionView.swift): three
// shelves at y = 0.65/0.75/0.85, two plants each, stepping right as the
// window's counter descends.
export const DEFAULT_SLOTS = [
  { x: 0.175, y: 0.675 },
  { x: 0.525, y: 0.675 },
  { x: 0.325, y: 0.775 },
  { x: 0.675, y: 0.775 },
  { x: 0.475, y: 0.875 },
  { x: 0.825, y: 0.875 },
];

const SPRITE_WIDTH_PERCENT = (160 / 390) * 100;
const ROOM_OBJECT_POSITION = "82% center";

// backgroundId prefixes map to the two base room images
const ROOM_BASE_IMAGES = {
  sunny: roomSunny,
  loft: roomLoft,
};

// Tint overlays replicate the iOS variants exactly (PlantCollectionView.swift
// RoomBackgroundCatalog): SwiftUI system colors, blend mode, opacity, plus the
// saturation/brightness adjustments applied to the base image itself.
const ROOM_TINTS = {
  warm: {
    background: "rgba(255, 149, 0, 0.35)", // .orange, softLight 0.35
    blend: "soft-light",
    filter: null,
  },
  cool: {
    background: "rgba(0, 122, 255, 0.30)", // .blue, softLight 0.30
    blend: "soft-light",
    filter: "saturate(0.85)",
  },
  night: {
    background: "rgba(88, 86, 214, 0.45)", // .indigo, multiply 0.45
    blend: "multiply",
    filter: "saturate(0.8) brightness(0.92)",
  },
};

/*******************************************************************************
 * @func roomVisuals
 * Resolves a room's backgroundId (e.g. "sunny-warm", "loft-night") to the base
 * image and tint overlay used to render the scene
 *
 * @param backgroundId (string): The room's backgroundId from the Users doc
 * @return Object: { image, tint } where tint may be null
 *
 * @notes Unknown ids fall back to the default sunny room
 *
 ******************************************************************************/
export const roomVisuals = (backgroundId) => {
  const [base, variant] = String(backgroundId || "sunny-day").split("-");
  return {
    image: ROOM_BASE_IMAGES[base] || roomSunny,
    tint: ROOM_TINTS[variant] || null,
  };
};

/*******************************************************************************
 * @func computeWindowPlacement
 * Splits a user's plants into the ones drawn in the window scene (with their
 * normalized positions) and the rest of the collection, mirroring the iOS
 * rules: drawable sprites only, plants keep their saved windowPosition, the
 * unplaced fill the default shelf slots, capped at 10 per room.
 *
 * @param plants (array): The user's living plants (with doc ids)
 * @param speciesMap (object): speciesId -> species doc data
 * @param currentRoom (object): The room being rendered
 * @param rooms (array): All the user's rooms (first room owns roomless plants)
 * @return Object: { placedPlants: [{plant, position}], otherPlants: [plant] }
 *
 * @notes None
 *
 ******************************************************************************/
export const computeWindowPlacement = (plants, speciesMap, currentRoom, rooms) => {
  if (!plants || !speciesMap) return { placedPlants: [], otherPlants: [] };

  const drawable = plants.filter((plant) =>
    plantHasSpriteArt(plant, speciesMap[plant.species])
  );

  const inRoom = (plant) =>
    plant.roomId === currentRoom.id ||
    (plant.roomId == null && currentRoom === rooms[0]);

  const hasValidPosition = (plant) =>
    Number.isFinite(plant.windowPosition?.x) &&
    Number.isFinite(plant.windowPosition?.y) &&
    plant.windowPosition.x >= 0 &&
    plant.windowPosition.x <= 1 &&
    plant.windowPosition.y >= 0 &&
    plant.windowPosition.y <= 1;

  const roomPlants = drawable.filter(inRoom);
  const placed = roomPlants
    .filter(hasValidPosition)
    .slice(0, WINDOW_PLANT_CAP)
    .map((plant) => ({ plant, position: plant.windowPosition }));

  const remainingCap = WINDOW_PLANT_CAP - placed.length;
  const defaulted = roomPlants
    .filter((plant) => !hasValidPosition(plant))
    .slice(0, Math.min(DEFAULT_SLOTS.length, Math.max(0, remainingCap)))
    .map((plant, index) => ({ plant, position: DEFAULT_SLOTS[index] }));

  const all = [...placed, ...defaulted];
  const placedIds = new Set(all.map(({ plant }) => plant.id));
  const rest = drawable.filter((plant) => !placedIds.has(plant.id));

  return { placedPlants: all, otherPlants: rest };
}; // End computeWindowPlacement

/*******************************************************************************
 * @func WindowPlant (React Component)
 * A single plant in the window scene: the sprite anchored bottom-center at its
 * normalized position, with a name pill underneath. Clicking navigates to the
 * plant's bio page.
 *
 * @param plant (object): Plant doc data (with id)
 * @param species (object): Species doc data for the plant
 * @param position (object): Normalized {x, y}
 * @param interactive (bool): Whether clicking navigates / hovers highlight
 * @return JSX Element
 *
 * @notes Position and z-order mirror the iOS layout: plants lower in the
 * window render on top
 *
 ******************************************************************************/
const WindowPlant = ({ plant, species, position, interactive = true }) => {
  const navigate = useNavigate();
  const { x, y } = position;

  return (
    <div
      className={`absolute group ${interactive ? "cursor-pointer" : ""}`}
      style={{
        left: `${x * 100}%`,
        top: `${y * 100}%`,
        width: `${SPRITE_WIDTH_PERCENT}%`,
        transform: "translate(-50%, -100%)",
        zIndex: Math.round(y * 100),
      }}
      onClick={interactive ? () => navigate("/plant/" + plant.id) : undefined}
      title={plant.name}
    >
      <PlantImage
        plant={plant}
        species={species}
        onLoadCallback={() => {}}
        width="100%"
      />
      {plant.name && plant.name.trim() && (
        <div className="flex justify-center -mt-1">
          <span className="bg-white/90 text-leaf-brown text-[11px] sm:text-xs font-global font-bold px-2.5 py-0.5 rounded-full shadow-sm border border-leaf-brown/10 whitespace-nowrap max-w-full overflow-hidden text-ellipsis group-hover:bg-leaf-green-soft transition-colors">
            {plant.name.trim()}
          </span>
        </div>
      )}
    </div>
  );
}; // End WindowPlant

/*******************************************************************************
 * @func WindowScene (React Component)
 * The room itself: background art, tint overlay, and the placed plants at
 * their normalized coordinates. Portrait aspect ratio matches the iOS canvas.
 *
 * @param room (object): { id, name, backgroundId }
 * @param plants (array): Placed plants [{plant, position}] for this room
 * @param speciesMap (object): speciesId -> species doc data
 * @param interactive (bool, optional): Pass false for thumbnail scenes
 * @return JSX Element
 *
 * @notes None
 *
 ******************************************************************************/
const WindowScene = ({ room, plants, speciesMap, interactive = true }) => {
  const { image, tint } = roomVisuals(room.backgroundId);

  return (
    <div
      className="relative w-full overflow-hidden rounded-3xl shadow-lift border-4 border-white"
      style={{ aspectRatio: "390 / 701" }}
    >
      <img
        src={image}
        alt=""
        className="absolute inset-0 w-full h-full object-cover"
        style={{
          objectPosition: ROOM_OBJECT_POSITION,
          filter: tint?.filter || "none",
        }}
      />
      {tint && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ backgroundColor: tint.background, mixBlendMode: tint.blend }}
        />
      )}
      {plants.map(({ plant, position }) => {
        const species = speciesMap[plant.species];
        if (!species) return null;
        return (
          <WindowPlant
            key={plant.id}
            plant={plant}
            species={species}
            position={position}
            interactive={interactive}
          />
        );
      })}
      {plants.length === 0 && (
        <div className="absolute inset-x-4 top-1/3 text-center font-global">
          <span className="bg-white/85 text-leaf-brown text-sm font-semibold px-4 py-2 rounded-full shadow-sm inline-block">
            No plants in this window yet 🌱
          </span>
        </div>
      )}
    </div>
  );
}; // End WindowScene

export default WindowScene;
